/**
 * Géométrie de la barre de progression segmentée et interpolation de la
 * micro-progression (§3.3).
 *
 * Ce module est volontairement pur — aucun accès au DOM, aucune dépendance
 * React — pour être testable directement sous Vitest. Toutes les bornes
 * proviennent de `@portail/shared` : rien n'est recopié ici.
 */

import {
  isAwaitingUser,
  isTerminal,
  PIPELINE_STEPS,
  pipelineStepsFor,
  PROGRESS_RANGES,
  STATE_LABELS,
  type DeploymentState,
  type DeploymentType,
  type PipelineStep,
} from '@portail/shared';

/** Début de la zone couverte par les segments (fin de la file d'attente). */
export const SEGMENTS_START = PROGRESS_RANGES[PIPELINE_STEPS[0]].start;

/** Fin de la zone couverte par les segments. */
export const SEGMENTS_END = PROGRESS_RANGES[PIPELINE_STEPS[PIPELINE_STEPS.length - 1]].end;

/**
 * Amplitude totale couverte par les segments. `QUEUED` (0–5 %) n'a pas de
 * segment propre : l'attente en file n'est pas une étape du pipeline, elle est
 * signalée par le libellé au-dessus de la barre.
 */
export const SEGMENTS_SPAN = SEGMENTS_END - SEGMENTS_START;

/**
 * Amplitude couverte par les segments d'un type de job donné.
 *
 * Un rollback ne traverse que `DEPLOYING` et `HEALTHCHECK` (§4.2) : sa barre
 * couvre 80–100 %, et ses deux segments se partagent cette largeur. Sans cela,
 * elle afficherait six segments dont quatre resteraient éternellement vides —
 * une barre qui promet un travail qui n'aura pas lieu.
 */
export function segmentsSpanFor(type: DeploymentType): number {
  const steps = pipelineStepsFor(type);
  return PROGRESS_RANGES[steps[steps.length - 1]].end - PROGRESS_RANGES[steps[0]].start;
}

/** État visuel d'un segment. */
export type SegmentStatus = 'done' | 'current' | 'pending' | 'failed';

/** Un segment de la barre de progression. */
export interface ProgressSegment {
  step: PipelineStep;
  /** Libellé issu de `STATE_LABELS`, jamais réécrit ici. */
  label: string;
  /** Bornes de progression globale de l'étape, en pourcentage. */
  start: number;
  end: number;
  /** Largeur du segment dans la barre, en pourcentage de la largeur totale. */
  widthPercent: number;
  status: SegmentStatus;
  /** Remplissage interne du segment, entre 0 et 1. */
  fill: number;
}

/** Entrée du calcul de géométrie. */
export interface SegmentsInput {
  state: DeploymentState;
  /** Progression globale courante, entre 0 et 100. */
  progress: number;
  /** Étape à laquelle le pipeline a échoué, le cas échéant. */
  failedStep: DeploymentState | null;
  /** Nature du job : elle détermine les étapes attendues (§4.2). */
  type?: DeploymentType;
}

/** Ramène une valeur dans l'intervalle `[min, max]`. */
export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/** Remplissage d'un intervalle `[start, end]` pour une progression donnée. */
function fillOf(start: number, end: number, progress: number): number {
  if (end <= start) return progress >= end ? 1 : 0;
  return clamp((progress - start) / (end - start), 0, 1);
}

/**
 * Construit la liste des segments : un par étape attendue pour ce type de job,
 * de largeur proportionnelle à l'amplitude de sa plage dans `PROGRESS_RANGES`.
 * Le `BUILDING` occupe donc plus du tiers de la barre d'un déploiement — c'est
 * bien l'étape la plus longue, la barre ne ment pas sur le temps restant. Un
 * rollback, lui, n'affiche que ses deux étapes réelles.
 */
export function buildSegments({
  state,
  progress,
  failedStep,
  type = 'DEPLOY',
}: SegmentsInput): ProgressSegment[] {
  const steps = pipelineStepsFor(type);
  const span = segmentsSpanFor(type);
  const currentIndex = steps.indexOf(state as PipelineStep);
  const failedIndex = failedStep ? steps.indexOf(failedStep as PipelineStep) : -1;

  return steps.map((step, index) => {
    const { start, end } = PROGRESS_RANGES[step];

    let status: SegmentStatus;
    if (state === 'DONE') {
      status = 'done';
    } else if (state === 'FAILED' && failedIndex !== -1) {
      status = index < failedIndex ? 'done' : index === failedIndex ? 'failed' : 'pending';
    } else if (state === 'FAILED' || state === 'CANCELLED' || isAwaitingUser(state)) {
      // Trois cas où aucune étape du pipeline n'est en cours : un échec dont
      // l'étape fautive n'a pas été identifiée, une annulation, et l'attente
      // d'une validation. On se fie alors à la progression atteinte — la seule
      // information disponible. Pour une attente de validation, elle vaut la fin
      // de la transformation : cette étape apparaît franchie, les suivantes non,
      // ce qui décrit exactement la situation.
      status = progress >= end ? 'done' : 'pending';
    } else if (currentIndex === -1) {
      // `QUEUED` : rien n'a encore commencé.
      status = 'pending';
    } else {
      status = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'pending';
    }

    // Un segment en échec est rempli intégralement : c'est lui qui porte la
    // couleur d'alerte, un remplissage partiel le rendrait illisible.
    let fill: number;
    if (status === 'done' || status === 'failed') fill = 1;
    else if (status === 'pending') fill = 0;
    else fill = fillOf(start, end, progress);

    return {
      step,
      label: STATE_LABELS[step],
      start,
      end,
      widthPercent: ((end - start) / span) * 100,
      status,
      fill,
    };
  });
}

// ---------------------------------------------------------------------------
// Micro-progression
// ---------------------------------------------------------------------------

/**
 * Vitesse de convergence vers la valeur reçue du serveur, par seconde.
 * Exprimée comme un taux de décroissance exponentielle : à 2,5, l'écart
 * restant est réduit de 92 % en une seconde — assez rapide pour paraître
 * réactif, assez progressif pour ne pas donner une impression de saut.
 */
export const EASING_RATE_PER_SECOND = 2.5;

/**
 * Vitesse de la dérive autonome, par seconde. Très lente : elle ne sert qu'à
 * prouver que le service travaille encore. À 0,006, la moitié de la marge
 * restante est consommée en environ deux minutes — la durée d'un build.
 */
export const DRIFT_RATE_PER_SECOND = 0.006;

/**
 * Marge, en points de pourcentage, laissée avant la fin du segment courant.
 * La dérive ne doit jamais donner l'impression que l'étape est terminée.
 */
export const DRIFT_MARGIN = 1.5;

/**
 * Plafond que la valeur affichée ne doit jamais franchir.
 *
 * Interprétation retenue du §3.3 : la valeur reçue du serveur est un
 * *plancher* d'animation (on l'atteint toujours), la fin de l'étape courante
 * — moins une marge — est le *plafond* de la dérive autonome. Sans cette
 * lecture, la dérive serait impossible : la cible serveur ne bouge pas pendant
 * un build de deux minutes, et la barre resterait figée, exactement ce que le
 * cahier des charges cherche à éviter.
 *
 * Sur un état terminal, plafond et cible se confondent : plus aucune dérive.
 */
export function displayCeiling(state: DeploymentState, target: number): number {
  // Un état terminal ne dérive plus : la valeur reçue est définitive. Pour un
  // échec ou une annulation, c'est **elle** qui fait le plafond et non la fin de
  // la plage — celle-ci valait 100 %, et l'animation montait donc jusqu'à une
  // barre pleine sous un titre annonçant l'échec.
  if (isTerminal(state)) return state === 'DONE' ? 100 : target;
  return Math.max(target, PROGRESS_RANGES[state].end - DRIFT_MARGIN);
}

/** Paramètres d'une image d'animation. */
export interface AnimationFrameInput {
  /** Valeur actuellement affichée. */
  displayed: number;
  /** Dernière valeur reçue du serveur. */
  target: number;
  /** Plafond, typiquement issu de `displayCeiling`. */
  ceiling: number;
  /** Temps écoulé depuis l'image précédente, en millisecondes. */
  elapsedMs: number;
}

/**
 * Calcule la prochaine valeur affichée.
 *
 * Garanties, vérifiées par les tests :
 * - le résultat ne recule jamais (`>= displayed`) ;
 * - il ne dépasse jamais le plafond ;
 * - la convergence vers la cible ne la dépasse jamais.
 */
export function nextDisplayedProgress({
  displayed,
  target,
  ceiling,
  elapsedMs,
}: AnimationFrameInput): number {
  // Un onglet remis au premier plan peut produire un delta énorme : on le
  // borne, sinon la barre bondirait d'un coup jusqu'au plafond.
  const seconds = clamp(elapsedMs, 0, 1000) / 1000;

  // Le plafond ne peut pas faire reculer la barre : la monotonie prime.
  const effectiveCeiling = Math.max(ceiling, displayed);

  // 1. Convergence exponentielle vers la cible serveur. Par construction, la
  //    valeur reste strictement du bon côté de la cible : jamais de dépassement.
  const easingFactor = 1 - Math.exp(-EASING_RATE_PER_SECOND * seconds);
  const eased = displayed + (target - displayed) * easingFactor;

  // 2. Dérive autonome vers le plafond, du même type : asymptotique, donc elle
  //    ralentit en approchant et ne colle jamais à la limite du segment.
  const driftFactor = 1 - Math.exp(-DRIFT_RATE_PER_SECOND * seconds);
  const drifted = displayed + (effectiveCeiling - displayed) * driftFactor;

  const value = Math.max(displayed, eased, drifted);
  return Math.min(value, effectiveCeiling);
}
